import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { TextInput } from "@/components/quote/Fields";

export default function StepJourney({ data, update, onNext, onBack }) {
  const j = data.journey;
  const setJ = (k, val) => update({ journey: { ...j, [k]: val } });
  const [checking, setChecking] = useState(false);
  const [mainland, setMainland] = useState(null); // null | true | false

  async function computeMiles() {
    if (!j.pickup_location || !j.dropoff_location) {
      toast.error("Please enter both pickup and drop-off locations.");
      return;
    }
    setChecking(true);
    try {
      const { data: r } = await publicApi.post("/distance", {
        origin: j.pickup_location,
        destination: j.dropoff_location,
      });
      if (!r.ok || r.miles == null) {
        toast.error(
          "We couldn’t calculate the route automatically. Try full postcodes, or use custom quote."
        );
        setMainland(null);
        return;
      }
      setJ("miles", r.miles);
      setMainland(r.mainland_uk);
      if (!r.mainland_uk) {
        toast.warning("Route is outside mainland GB — this will need a custom quote.");
      } else {
        toast.success(`Route: ${r.miles.toFixed(1)} miles`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Route lookup failed. Please try again.");
    } finally {
      setChecking(false);
    }
  }

  const canNext = j.miles > 0 && !checking;

  return (
    <motion.div
      key="step-journey"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-8"
    >
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
          Step 02 · Journey
        </div>
        <h1 className="font-display font-extrabold text-white text-3xl md:text-5xl leading-[1.05] tracking-tight">
          Where are we going?
        </h1>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <TextInput
          label="Collection location *"
          hint="Full address or postcode"
          testId="quote-input-pickup"
          placeholder="e.g. DE14 1JS or 12 Any St, Burton"
          value={j.pickup_location}
          onChange={(e) => setJ("pickup_location", e.target.value)}
        />
        <TextInput
          label="Delivery location *"
          testId="quote-input-dropoff"
          placeholder="e.g. M1 1AA or Deansgate, Manchester"
          value={j.dropoff_location}
          onChange={(e) => setJ("dropoff_location", e.target.value)}
        />
      </div>

      <button
        type="button"
        onClick={computeMiles}
        disabled={checking || !j.pickup_location || !j.dropoff_location}
        data-testid="quote-compute-miles"
        className="btn-ghost w-full sm:w-auto justify-center disabled:opacity-40"
      >
        {checking ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" /> Calculating route…
          </>
        ) : (
          <>
            <MapPin className="w-5 h-5" /> Calculate driving distance
          </>
        )}
      </button>

      {j.miles > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-white/10 bg-[#0A0A0A] p-6 flex items-center justify-between gap-4"
          data-testid="quote-miles-card"
        >
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
              Loaded miles
            </div>
            <div className="font-display font-extrabold text-white text-4xl mt-1">
              {j.miles.toFixed(1)}
            </div>
          </div>
          <ArrowRight className="w-6 h-6 text-[#E60000]" />
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
              Coverage
            </div>
            <div
              className={`mt-1 font-display font-bold text-lg ${
                mainland ? "text-white" : "text-[#E60000]"
              }`}
            >
              {mainland ? "Mainland GB" : "Custom quote needed"}
            </div>
          </div>
        </motion.div>
      )}

      <div className="flex justify-between pt-2 gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="quote-journey-back"
          className="btn-ghost"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canNext}
          data-testid="quote-journey-next"
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    </motion.div>
  );
}
